import React, { useState } from 'react';
import { Button } from '@shared/components/ui/Button';
import { supabase } from '@shared/lib/supabase';
import { useAuth } from '../contexts/AuthContext';

interface DiagnosisResult {
  success: boolean;
  diagnosis?: any;
  summary?: any;
  error?: string;
}

const DiagnosticTest: React.FC = () => {
  const { user } = useAuth();
  const [isRunning, setIsRunning] = useState(false);
  const [result, setResult] = useState<DiagnosisResult | null>(null);

  const runDiagnostics = async () => {
    setIsRunning(true);
    setResult(null);

    try {
      console.log('🔍 Starting comprehensive extraction diagnostics...');

      const diagnosis: any = {
        timestamp: new Date().toISOString(),
        documents_analysis: {},
        extraction_jobs_analysis: {},
        storage_analysis: {},
        content_quality_analysis: {},
        failure_patterns: [],
        recommendations: []
      };

      // 1. Query documents table
      console.log('📊 Step 1: Analyzing documents table');
      const { data: allDocuments, error: docsError } = await supabase
        .from('documents')
        .select('*')
        .order('created_at', { ascending: false });

      if (docsError) {
        diagnosis.documents_analysis.error = docsError.message;
      } else {
        const docs = allDocuments || [];
        const statusCounts: Record<string, number> = {};
        const extractionStatusCounts: Record<string, number> = {};
        const methodCounts: Record<string, number> = {};
        const contentLengthStats: number[] = [];
        const problemDocuments: any[] = [];

        for (const doc of docs) {
          statusCounts[doc.status] = (statusCounts[doc.status] || 0) + 1;
          extractionStatusCounts[doc.text_extraction_status || 'null'] =
            (extractionStatusCounts[doc.text_extraction_status || 'null'] || 0) + 1;
          methodCounts[doc.extraction_method || 'none'] =
            (methodCounts[doc.extraction_method || 'none'] || 0) + 1;

          const contentLength = doc.extracted_content?.length || 0;
          contentLengthStats.push(contentLength);

          const problems = [];
          if (doc.status === 'error') problems.push('error_status');
          if (doc.text_extraction_status === 'failed') problems.push('extraction_failed');
          if (!doc.extracted_content) problems.push('null_content');
          if (contentLength < 50) problems.push('insufficient_content');
          if (contentLength > 0 && contentLength < 200) problems.push('very_short_content');
          if (doc.extraction_error_message) problems.push('has_error_message');

          if (problems.length > 0) {
            problemDocuments.push({
              id: doc.id,
              name: doc.name,
              type: doc.type,
              status: doc.status,
              text_extraction_status: doc.text_extraction_status,
              extraction_method: doc.extraction_method,
              content_length: contentLength,
              problems,
              error_message: doc.extraction_error_message,
              created_at: doc.created_at
            });
          }
        }

        const validLengths = contentLengthStats.filter(l => l > 0);
        const avgLength = validLengths.length > 0
          ? Math.round(validLengths.reduce((a, b) => a + b, 0) / validLengths.length)
          : 0;

        diagnosis.documents_analysis = {
          total_documents: docs.length,
          status_distribution: statusCounts,
          extraction_status_distribution: extractionStatusCounts,
          extraction_method_distribution: methodCounts,
          content_length_stats: {
            average: avgLength,
            min: validLengths.length > 0 ? Math.min(...validLengths) : 0,
            max: validLengths.length > 0 ? Math.max(...validLengths) : 0,
            documents_with_content: validLengths.length,
            documents_without_content: docs.length - validLengths.length
          },
          problem_documents: {
            count: problemDocuments.length,
            percentage: docs.length > 0 ? Math.round((problemDocuments.length / docs.length) * 100) : 0,
            details: problemDocuments.slice(0, 10)
          },
          recent_uploads: docs.slice(0, 5).map(d => ({
            id: d.id,
            name: d.name,
            status: d.status,
            text_extraction_status: d.text_extraction_status,
            content_length: d.extracted_content?.length || 0,
            created_at: d.created_at
          }))
        };
      }

      // 2. Query extraction_jobs table
      console.log('📊 Step 2: Analyzing extraction_jobs table');
      const { data: extractionJobs, error: jobsError } = await supabase
        .from('extraction_jobs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100);

      if (jobsError) {
        diagnosis.extraction_jobs_analysis = {
          table_exists: false,
          error: jobsError.message
        };
      } else {
        const jobs = extractionJobs || [];
        const jobStatusCounts: Record<string, number> = {};
        const errorTypeCounts: Record<string, number> = {};

        for (const job of jobs) {
          jobStatusCounts[job.status] = (jobStatusCounts[job.status] || 0) + 1;
          if (job.error_type) {
            errorTypeCounts[job.error_type] = (errorTypeCounts[job.error_type] || 0) + 1;
          }
        }

        diagnosis.extraction_jobs_analysis = {
          table_exists: true,
          total_jobs: jobs.length,
          status_distribution: jobStatusCounts,
          error_type_distribution: errorTypeCounts,
          recent_jobs: jobs.slice(0, 5).map(j => ({
            id: j.id,
            status: j.status,
            error_type: j.error_type,
            created_at: j.created_at
          }))
        };
      }

      // 3. Storage bucket check
      console.log('📊 Step 3: Checking storage bucket');
      const { data: bucketFiles, error: storageError } = await supabase
        .storage
        .from('documents')
        .list('', { limit: 10 });

      diagnosis.storage_analysis = storageError
        ? { accessible: false, error: storageError.message }
        : { accessible: true, files_count: bucketFiles?.length || 0 };

      // 4. Generate recommendations
      const { documents_analysis, extraction_jobs_analysis } = diagnosis;

      if (!extraction_jobs_analysis.table_exists || extraction_jobs_analysis.total_jobs === 0) {
        diagnosis.recommendations.push({
          priority: "HIGH",
          category: "Architecture",
          issue: "Extraction jobs system not in use",
          recommendation: "The job queue system is not being utilized",
          action: "Integrate extraction-worker Edge Function"
        });
      }

      if (documents_analysis.problem_documents?.percentage > 20) {
        diagnosis.recommendations.push({
          priority: "CRITICAL",
          category: "Extraction Quality",
          issue: `${documents_analysis.problem_documents.percentage}% of documents have issues`,
          recommendation: "High failure rate indicates systematic problems",
          action: "Review extraction methods and enhance text cleaning"
        });
      }

      const summary = {
        total_documents: documents_analysis.total_documents || 0,
        documents_with_issues: documents_analysis.problem_documents?.count || 0,
        problem_rate_percentage: documents_analysis.problem_documents?.percentage || 0,
        extraction_jobs_in_use: extraction_jobs_analysis.table_exists && extraction_jobs_analysis.total_jobs > 0,
        storage_accessible: diagnosis.storage_analysis.accessible || false,
        recommendations_count: diagnosis.recommendations.length,
        critical_issues: diagnosis.recommendations.filter((r: any) => r.priority === "CRITICAL").length,
        high_priority_issues: diagnosis.recommendations.filter((r: any) => r.priority === "HIGH").length
      };

      console.log('✅ Diagnostics completed', summary);

      setResult({
        success: true,
        diagnosis,
        summary
      });

    } catch (error: any) {
      console.error('❌ Diagnostics failed', error);
      setResult({
        success: false,
        error: error.message
      });
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-6">
          Extraction System Diagnostics
        </h1>

        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <p className="text-gray-600 mb-4">
            Run comprehensive diagnostics to analyze the PDF extraction system, identify issues, and get recommendations.
          </p>
          <Button onClick={runDiagnostics} disabled={isRunning}>
            {isRunning ? 'Running Diagnostics...' : 'Run Diagnostics'}
          </Button>
        </div>

        {result && (
          <div className="space-y-6">
            {/* Summary Card */}
            {result.success && result.summary && (
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h2 className="text-xl font-semibold mb-4">Summary</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <div className="text-2xl font-bold text-blue-900">
                      {result.summary.total_documents}
                    </div>
                    <div className="text-sm text-blue-700">Total Documents</div>
                  </div>
                  <div className="p-4 bg-yellow-50 rounded-lg">
                    <div className="text-2xl font-bold text-yellow-900">
                      {result.summary.documents_with_issues}
                    </div>
                    <div className="text-sm text-yellow-700">With Issues</div>
                  </div>
                  <div className="p-4 bg-red-50 rounded-lg">
                    <div className="text-2xl font-bold text-red-900">
                      {result.summary.problem_rate_percentage}%
                    </div>
                    <div className="text-sm text-red-700">Failure Rate</div>
                  </div>
                  <div className="p-4 bg-green-50 rounded-lg">
                    <div className="text-2xl font-bold text-green-900">
                      {result.summary.extraction_jobs_in_use ? 'Yes' : 'No'}
                    </div>
                    <div className="text-sm text-green-700">Jobs System</div>
                  </div>
                </div>
              </div>
            )}

            {/* Recommendations */}
            {result.success && result.diagnosis?.recommendations?.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h2 className="text-xl font-semibold mb-4">Recommendations</h2>
                <div className="space-y-4">
                  {result.diagnosis.recommendations.map((rec: any, idx: number) => (
                    <div
                      key={idx}
                      className={`p-4 rounded-lg border-l-4 ${
                        rec.priority === 'CRITICAL'
                          ? 'bg-red-50 border-red-500'
                          : rec.priority === 'HIGH'
                          ? 'bg-orange-50 border-orange-500'
                          : rec.priority === 'MEDIUM'
                          ? 'bg-yellow-50 border-yellow-500'
                          : 'bg-blue-50 border-blue-500'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="font-semibold">{rec.issue}</h3>
                        <span className="px-2 py-1 text-xs font-medium rounded bg-white">
                          {rec.priority}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700 mb-2">{rec.recommendation}</p>
                      <p className="text-sm font-medium text-gray-900">
                        Action: {rec.action}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Detailed Results */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-xl font-semibold mb-4">Detailed Results</h2>
              <pre className="bg-gray-50 p-4 rounded-lg overflow-auto text-xs">
                {JSON.stringify(result, null, 2)}
              </pre>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DiagnosticTest;
